/**
 * Poignée verticale de la colonne des titres : glisser (souris/tactile) ou flèches gauche/droite.
 * Composant contrôlé : `width` vient du parent, `onResize(width, definitif)` le met à jour ;
 * `definitif` vaut `true` au relâchement ou à chaque appui clavier (moment de persister).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { clampSidebarWidth, SIDEBAR_KEY_STEP, SIDEBAR_MAX, SIDEBAR_MIN } from './sidebar-width.js';

export function SidebarResizer({ width, maxWidth = SIDEBAR_MAX, onResize, label }) {
  const [active, setActive] = useState(false);
  // Annule le glisser en cours (écouteurs, image en attente) sans rien émettre.
  const stopDragRef = useRef(null);

  // Démontage en plein glisser : plus aucun écouteur de `window` ne doit survivre.
  useEffect(() => () => stopDragRef.current?.(), []);

  const handlePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      stopDragRef.current?.();
      const startX = event.clientX;
      const startWidth = width;
      const grip = event.currentTarget;
      const { pointerId } = event;
      let pendingWidth = null;
      let frame = null;
      setActive(true);

      // Le pointeur est capturé quand l'environnement le permet : le glisser continue hors
      // de la poignée et un geste annulé envoie `pointercancel`.
      try {
        grip.setPointerCapture?.(pointerId);
      } catch {
        // Pointeur déjà libéré : les écouteurs de `window` suffisent.
      }

      // Mises à jour regroupées par image : un seul rendu par frame pendant le glisser.
      function flush() {
        frame = null;
        if (pendingWidth !== null) onResize(pendingWidth, false);
        pendingWidth = null;
      }

      function compute(clientX) {
        return Math.min(maxWidth, clampSidebarWidth(startWidth + clientX - startX));
      }

      function onMove(moveEvent) {
        pendingWidth = compute(moveEvent.clientX);
        if (frame === null) frame = window.requestAnimationFrame(flush);
      }

      function teardown() {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onCancel);
        if (frame !== null) window.cancelAnimationFrame(frame);
        frame = null;
        pendingWidth = null;
        stopDragRef.current = null;
        try {
          grip.releasePointerCapture?.(pointerId);
        } catch {
          // Déjà relâché.
        }
      }

      function onUp(upEvent) {
        teardown();
        setActive(false);
        onResize(compute(upEvent.clientX), true);
      }

      // Geste annulé : retour à la largeur d'avant le glisser, rien n'est persisté.
      function onCancel() {
        teardown();
        setActive(false);
        onResize(startWidth, false);
      }

      stopDragRef.current = teardown;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onCancel);
    },
    [width, maxWidth, onResize],
  );

  const handleKeyDown = useCallback(
    (event) => {
      let next = null;
      if (event.key === 'ArrowLeft') next = width - SIDEBAR_KEY_STEP;
      else if (event.key === 'ArrowRight') next = width + SIDEBAR_KEY_STEP;
      else if (event.key === 'Home') next = SIDEBAR_MIN;
      else if (event.key === 'End') next = SIDEBAR_MAX;
      if (next === null) return;
      event.preventDefault();
      onResize(Math.min(maxWidth, clampSidebarWidth(next)), true);
    },
    [width, maxWidth, onResize],
  );

  return (
    <div className="gantt__resizer">
      <div
        className="gantt__resizer-grip"
        role="separator"
        aria-orientation="vertical"
        aria-label={label}
        aria-valuenow={width}
        aria-valuemin={SIDEBAR_MIN}
        aria-valuemax={maxWidth}
        tabIndex={0}
        data-active={active}
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
